import styled from 'styled-components';

import { colors } from 'styles/theme/colors';
import { BodySmallBold } from 'styles/typography';
import CircleCheckIcon from 'svg/CircleCheckIcon';

import BenefitsByStc from './BenefitsByStc';
import { EligibilityResult, primaryCheck } from './eligibilityData';
import EligibilityOverview from './EligibilityOverview';
import SkilledNursingSection from './SkilledNursingSection';

// A completed eligibility result: an active/inactive banner, the subscriber+payer Overview, and
// the STC-grouped benefits table. `active` distinguishes the two success states.

interface Props {
  result: EligibilityResult;
  active: boolean;
  payerLogo?: string | null;
}

export default function EligibilityResultView({ result, active, payerLogo }: Props) {
  const stedi = primaryCheck(result)?.stediResponse ?? {};

  return (
    <>
      {active ? (
        <Banner $tone='success' data-testid='coverage-banner'>
          <CircleCheckIcon width={20} height={20} fill={colors.accentGreen} />
          Patient has been found with active coverage
        </Banner>
      ) : (
        <Banner $tone='danger' data-testid='coverage-banner'>
          <Minus aria-hidden>–</Minus>
          Patient was identified with no active coverage for this payer
        </Banner>
      )}

      <EligibilityOverview stedi={stedi} summary={result.summary} inactive={!active} payerLogo={payerLogo} />
      <SkilledNursingSection snf={result.summary?.skilledNursing} />
      <BenefitsByStc stedi={stedi} planName={stedi.subscriber?.planNumber} />
    </>
  );
}

const Banner = styled(BodySmallBold)<{ $tone: 'success' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 8px;
  font-size: 15px;
  color: ${({ $tone }) => ($tone === 'success' ? colors.accentGreen : colors.accentRed)};
  background: ${({ $tone }) => ($tone === 'success' ? colors.accentGreen05 : colors.accentRed05)};
  border: 1px solid ${({ $tone }) => ($tone === 'success' ? colors.accentGreen : colors.accentRed)};

  > svg {
    flex-shrink: 0;
    color: ${colors.accentGreen};
  }
`;

const Minus = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${colors.accentRed};
  color: ${colors.white};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  line-height: 1;
`;
